import { EnrollmentType, StudentType, Unenrollment as UnenrollmentType } from '@wonderschool/common-base-types';
import { Button, Dialog } from '@wonderschool/common-base-ui';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { EnrollmentFormDataType, EnrollmentStatusEnum } from '../../../enrollments';
import { dateFormatter, toDateObject } from '../../../helpers/dates';
import { useOrganization } from '../../../hooks/useOrganizations';
import { useMarketplaceAPI } from '../../../integrations';
import { studentSelected } from '../../../redux/actions/studentsActions';
import { saveStudent } from '../../studentsAPI';
import { useSelectedStudent, useStudentEnrollment } from '../../studentsHooks';
import { StudentFormUnenrollmentReasons } from './StudentFormUnenrollmentReasons';

interface UnenrollDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onDone?: () => void; // called only after successful unenroll
}

type UnenrollErrors = {
  unenrollmentDate?: string;
  unenrollmentInitiatedBy?: string;
  unenrollmentReason?: string;
};

export function UnenrollDialog({ isOpen, onClose, onDone }: UnenrollDialogProps) {
  const { t } = useTranslation();
  const organization = useOrganization();
  const selectedStudent = useSelectedStudent();
  const { enrollmentDate, unenrollmentData } = useStudentEnrollment(selectedStudent);
  const { sendEnrollmentStatus } = useMarketplaceAPI();
  const dispatch = useDispatch();

  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<EnrollmentFormDataType>({});
  const [errors, setErrors] = useState<UnenrollErrors>({});

  // Prefill from current student when dialog opens
  useEffect(() => {
    if (!isOpen) return;
    setFormData({
      unenrollmentInitiatedBy: unenrollmentData?.initiatedBy ?? '',
      unenrollmentReason: unenrollmentData?.reason ?? '',
      unenrollmentDate: unenrollmentData?.date ? (dateFormatter(unenrollmentData.date) ?? '') : '',
    });
    setErrors({});
  }, [isOpen, unenrollmentData]);

  const translatedErrors = useMemo(
    () =>
      Object.entries(errors)
        .map(([key, value]) => ({ [key]: value ? t(value) : undefined }))
        .reduce((acc, curr) => Object.assign(acc, curr), {} as Record<string, string | undefined>),
    [errors, t]
  );

  const onChangeDate = useCallback((_: any, { name, value, valid }: any) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (valid || isEmpty(value)) {
      setErrors((prev) => omit(prev, name));
    } else {
      setErrors((prev) => ({ ...prev, [name]: 'Invalid Date' }));
    }
  }, []);

  const validate = useCallback((): boolean => {
    const nextErrors: UnenrollErrors = {};
    if (!formData.unenrollmentDate) {
      nextErrors.unenrollmentDate = 'students.enrollment.validation.unenrollmentDateRequired';
    }
    // Disallow same-day unenrollment and unenrollment earlier than enrollment date
    if (formData.unenrollmentDate && enrollmentDate) {
      const unenroll = toDateObject(formData.unenrollmentDate)!;
      const enroll = toDateObject(enrollmentDate)!;
      const isSameDay = unenroll.toDateString() === enroll.toDateString();
      const isBefore = unenroll.getTime() < enroll.getTime();
      if (isSameDay || isBefore) {
        nextErrors.unenrollmentDate = 'students.enrollment.validation.unenrollmentDateMustBeAfterEnrollmentDate';
      }
    }
    setErrors(nextErrors);
    return isEmpty(nextErrors);
  }, [formData.unenrollmentDate, enrollmentDate]);

  const handleUnenroll = useCallback(async () => {
    if (!selectedStudent || !organization?.id) return;
    if (!validate()) return;

    try {
      setIsSaving(true);
      // Determine status based on unenrollment date
      const selectedDate = formData.unenrollmentDate ? new Date(formData.unenrollmentDate) : null;
      const now = new Date();
      const shouldRemainEnrolled = !!selectedDate && selectedDate > now;
      const newStatus = shouldRemainEnrolled ? EnrollmentStatusEnum.ENROLLED : EnrollmentStatusEnum.UNENROLLED;

      const unenrolled: UnenrollmentType = {
        initiatedBy: formData.unenrollmentInitiatedBy ?? '',
        reason: formData.unenrollmentReason ?? '',
        date: toDateObject(formData.unenrollmentDate)!,
        notes: formData.unenrollmentNotes ?? '',
      };

      const enrollment: EnrollmentType = {
        ...(selectedStudent.enrollment || {}),
        status: newStatus,
        unenrolled: unenrolled,
      };

      const data: Partial<StudentType> = {
        id: selectedStudent.id,
        enrollmentStatus: newStatus === EnrollmentStatusEnum.ENROLLED, // legacy boolean follows status
        enrollment: enrollment,
      };

      await saveStudent(organization.id, data);

      // Optimistically update selected student in Redux for immediate UI reflection
      const updatedStudent: StudentType = {
        ...(selectedStudent satisfies StudentType),
        enrollmentStatus: data.enrollmentStatus ?? selectedStudent.enrollmentStatus,
        enrollment: {
          ...(selectedStudent.enrollment || {}),
          ...(data.enrollment || {}),
        } satisfies EnrollmentType,
      };
      dispatch(studentSelected(updatedStudent));

      const interestedId = selectedStudent?.enrollment?.interestedFamily?.id;
      if (interestedId) {
        await sendEnrollmentStatus(EnrollmentStatusEnum.UNENROLLED, interestedId);
      }

      if (onDone) onDone();
      onClose();
    } catch (e) {
      setErrors((prev) => ({
        ...prev,
        unenrollmentDate: prev.unenrollmentDate || t('enrollments.unenroll.genericError'),
      }));
    } finally {
      setIsSaving(false);
    }
  }, [dispatch, formData, onClose, organization?.id, selectedStudent, sendEnrollmentStatus, validate, t, onDone]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      hasCloseButton={true}
      fullScreenOnMobile={true}
      extraDialogWrapperClasses="h-full"
    >
      <Dialog.Panel>
        <Dialog.Title>{t('enrollments.unenrollStudentTitle')}</Dialog.Title>
        <Dialog.Description>{t('enrollments.unenroll.confirmation')}</Dialog.Description>
        <div className="mt-4 w-full overflow-visible">
          <StudentFormUnenrollmentReasons
            setFormData={setFormData}
            setErrors={setErrors}
            onChangeDate={onChangeDate}
            errors={translatedErrors}
            unenrollmentReason={formData?.unenrollmentReason}
            unenrollmentDate={formData?.unenrollmentDate}
            unenrollmentInitiatedBy={formData?.unenrollmentInitiatedBy}
            enrollmentDate={enrollmentDate}
          />
        </div>
      </Dialog.Panel>

      <Dialog.Actions>
        <div className="grid w-full grid-cols-1 items-center gap-3 sm:grid-cols-2">
          <Button onClick={onClose} label={t('common.cancel')} extraClasses="w-full order-2 sm:order-1" />
          <Button
            onClick={handleUnenroll}
            primary
            label={t('enrollments.unenroll')}
            disabled={isSaving || !formData.unenrollmentDate}
            loading={isSaving}
            data-testid="unenroll-confirm-btn"
            extraClasses="w-full order-1 sm:order-2 bg-red-500 text-white hover:bg-red-600"
          />
        </div>
      </Dialog.Actions>
    </Dialog>
  );
}
